import { useAuth } from '@/contexts/auth-context';
import { firebaseKeys } from '@/query/firebase';
import { useActiveConnection, useClearActiveConnection } from '@/query/gcp';
import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ConnectionContextType {
  connectionId: string | null;
  setConnectionId: (id: string | null) => void;
  clearActiveConnection: () => Promise<void>;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return context;
};

interface ConnectionProviderProps {
  children: ReactNode;
}

export const ConnectionProvider: React.FC<ConnectionProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectionId, setConnectionIdState] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();
  const { user, oauthStatus } = useAuth();
  const { data: activeConnectionData } = useActiveConnection({ enabled: !!user });
  const clearActiveConnectionMutation = useClearActiveConnection();

  const activeConnection =
    user && oauthStatus?.status === 'connected' ? (activeConnectionData ?? null) : null;

  // Derive connectionId from activeConnection (always takes priority) or URL params/localStorage
  useEffect(() => {
    if (activeConnection) {
      const gcpConnectionId = `gcp-${activeConnection.projectId}__${activeConnection.databaseId}`;
      if (connectionId !== gcpConnectionId) {
        queueMicrotask(() => {
          setConnectionIdState(gcpConnectionId);
          localStorage.setItem('lastConnectionId', gcpConnectionId);
          const newParams = new URLSearchParams(searchParams);
          newParams.set('connectionId', gcpConnectionId);
          setSearchParams(newParams, { replace: true });
        });
      }
      if (!initialized) {
        queueMicrotask(() => setInitialized(true));
      }
      return;
    }

    if (initialized) {
      if (connectionId?.startsWith('gcp-')) {
        queueMicrotask(() => {
          setConnectionIdState(null);
          localStorage.removeItem('lastConnectionId');
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('connectionId');
          setSearchParams(newParams, { replace: true });
        });
      }
      return;
    }

    const urlConnectionId = searchParams.get('connectionId');

    if (urlConnectionId) {
      if (urlConnectionId.startsWith('gcp-')) {
        queueMicrotask(() => {
          setConnectionIdState(null);
          setInitialized(true);
        });
        return;
      }
      queueMicrotask(() => {
        setConnectionIdState(urlConnectionId);
        localStorage.setItem('lastConnectionId', urlConnectionId);
        setInitialized(true);
      });
    } else {
      const lastConnectionId = localStorage.getItem('lastConnectionId');
      if (lastConnectionId) {
        if (lastConnectionId.startsWith('gcp-')) {
          queueMicrotask(() => setInitialized(true));
          localStorage.removeItem('lastConnectionId');
          return;
        }
        queueMicrotask(() => {
          const orderedParams = new URLSearchParams();
          orderedParams.set('connectionId', lastConnectionId);
          const collection = searchParams.get('collection');
          const doc = searchParams.get('doc');
          if (collection) orderedParams.set('collection', collection);
          if (doc) orderedParams.set('doc', doc);
          setSearchParams(orderedParams, { replace: true });
          setConnectionIdState(lastConnectionId);
          setInitialized(true);
        });
      } else {
        queueMicrotask(() => setInitialized(true));
      }
    }
  }, [activeConnection, connectionId, searchParams, setSearchParams, initialized]);

  // Clear collections and documents when connection/database changes (they are tied to it)
  useEffect(() => {
    queryClient.removeQueries({ queryKey: firebaseKeys.all });
  }, [connectionId, queryClient]);

  const setConnectionId = (id: string | null) => {
    setConnectionIdState(id);

    if (id) {
      localStorage.setItem('lastConnectionId', id);
      const newParams = new URLSearchParams();
      newParams.set('connectionId', id);
      setSearchParams(newParams, { replace: true });
    } else {
      localStorage.removeItem('lastConnectionId');
      const newParams = new URLSearchParams();
      setSearchParams(newParams, { replace: true });
    }
  };

  const clearActiveConnection = async () => {
    try {
      await clearActiveConnectionMutation.mutateAsync();
      if (connectionId?.startsWith('gcp-')) {
        setConnectionIdState(null);
        localStorage.removeItem('lastConnectionId');
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('connectionId');
        setSearchParams(newParams, { replace: true });
      }
    } catch (error) {
      console.error('Error clearing active connection:', error);
      throw error;
    }
  };

  const value: ConnectionContextType = {
    connectionId,
    setConnectionId,
    clearActiveConnection,
  };

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
};
